import { useAuth0 } from '@auth0/auth0-react';
import { LogoutOptions } from '@auth0/auth0-spa-js';
import { AppFooter } from '@risk-first/ui-app-footer';
import { AppHeader } from '@risk-first/ui-app-header';
import { Loading } from '@risk-first/ui-loading';
import React, { Suspense, useCallback, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ReactComponent as AdminToolsLogoReactComponent } from '../assets/logos/PFaroeAdminTools.svg';
import { AccessToken } from '../auth/AccessToken';
import { useProtectedPages } from '../lib/private-route';
import { usePortfolioSetupOldFeature } from '../split/hooks';
import { APP_HEADER_HEIGHT } from '../theme';
import { ContentContainer, ContentWrapper, PageLoader } from './DefaultContent.style';
import { Navigation } from './Navigation';

export const DefaultContent = () => {
  const { isAuthenticated, user, logout } = useAuth0();
  const logo = React.useMemo(() => <AdminToolsLogoReactComponent width={150} />, []);
  const [isSidebarVisible, setSidebarVisibility] = useState(false);
  const handleOpenMenuClick = useCallback(() => setSidebarVisibility(!isSidebarVisible), [isSidebarVisible]);

  const portfolioSetupOld = usePortfolioSetupOldFeature();

  const handleLogout = useCallback(() => {
    const returnTo = `${window.location.origin}/logout`;
    const logoutOptions: LogoutOptions = {
      returnTo,
    };
    logout(logoutOptions);
  }, [logout]);

  const {
    ProtectedCurveListPage,
    ProtectedCurveDetailsPage,
    ProtectedMarketDataReportPage,
    ProtectedPricingPage,
    ProtectedAssetDetailsPage,
    ProtectedAssetMasterPage,
    ProtectedPortfolioSetupPage2Detail,
    ProtectedPortfolioSetupPage2,
    ProtectedPortfoliosPage,
    ProtectedIndexes,
    ProtectedValuationRunDetailsPage,
    ProtectedValuationRuns,
    ProtectedValuationRangeRuns,
    ProtectedClassificationRunDetailsPage,
    ProtectedRiskRunParentPage,
    ProtectedRiskRunDetailsPage,
    ProtectedSplitDetailPage,
    ProtectedSplitFlagsPage,
    ProtectedEntities,
    ProtectedEntityMaster,
    ProtectedDataImport,
  } = useProtectedPages(isSidebarVisible);

  return (
    <>
      <AppHeader
        hideMenuButton
        height={`${APP_HEADER_HEIGHT}px`}
        hideProfileMenu={!isAuthenticated}
        logo={logo}
        user={user ? { name: user?.name ?? '', picture: user?.picture ?? '' } : undefined}
        variant="gradient"
        onLogout={handleLogout}
        onOpenMenuClick={handleOpenMenuClick}
      />
      <Navigation>
        <ContentContainer>
          <ContentWrapper>
            <Suspense
              fallback={
                <PageLoader>
                  <Loading />
                </PageLoader>
              }
            >
              <Routes>
                <Route index element={<Navigate replace to="/curve-list" />} />
                <Route Component={ProtectedCurveDetailsPage} path="/curve-list/:curveName" />
                <Route Component={ProtectedCurveListPage} path="/curve-list" />
                <Route Component={ProtectedMarketDataReportPage} path="/market-data-report/:marketDataReportId?" />
                <Route Component={ProtectedPricingPage} path="/pricing" />
                <Route Component={ProtectedAssetDetailsPage} path="/asset-master/:id" />
                <Route Component={ProtectedAssetMasterPage} path="/asset-master" />
                {/* Redirect /portfolio-setup-v2/:workflowId to /portfolio-setup/:workflowId */}
                <Route
                  element={<Navigate replace to="/portfolio-setup/:workflowId" />}
                  path="/portfolio-setup-v2/:workflowId"
                />
                {/* Redirect /portfolio-setup-v2 to /portfolio-setup */}
                <Route element={<Navigate replace to="/portfolio-setup" />} path="/portfolio-setup-v2" />
                <Route Component={ProtectedPortfolioSetupPage2Detail} path="/portfolio-setup/:workflowId" />
                <Route Component={ProtectedPortfolioSetupPage2} path="/portfolio-setup" />
                <Route Component={ProtectedPortfoliosPage} path="/portfolios" />
                <Route Component={ProtectedIndexes} path="/indexes" />
                <Route Component={ProtectedValuationRunDetailsPage} path="/valuation-runs/:runId" />
                {portfolioSetupOld ? <Route Component={ProtectedValuationRuns} path="/valuation-runs" /> : null}
                {portfolioSetupOld ? (
                  <Route Component={ProtectedValuationRangeRuns} path="/valuation-range-runs" />
                ) : null}
                <Route Component={ProtectedClassificationRunDetailsPage} path="/classification-runs/:runId" />
                <Route Component={ProtectedRiskRunParentPage} path="/risk-runs/:riskRunId" />
                <Route Component={ProtectedRiskRunDetailsPage} path="/risk-details/:riskRunId/:valuationRunId" />
                <Route Component={ProtectedSplitDetailPage} path="/split-flags/:wsId/:splitName" />
                <Route Component={ProtectedSplitFlagsPage} path="/split-flags" />
                <Route Component={ProtectedEntities} path="/entities" />
                <Route Component={ProtectedEntityMaster} path="/entity-master/*" />
                <Route Component={ProtectedDataImport} path="/data-import/*" />
              </Routes>
            </Suspense>
          </ContentWrapper>
          <AppFooter variant="light" />
        </ContentContainer>
      </Navigation>
      <AccessToken />
    </>
  );
};
